import { useState, useEffect } from "react";
import Input from "../../components/Input";
import { Button } from "../../components/Button";
import Dropdown from "../../components/Dropdown";
import { TiDelete } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import { Card } from "@material-tailwind/react";
import { getAllTemplates, createTemplate } from "../../api/templateApi";
import { formatLabel } from "../../uitls/Utils";
import Swal from "sweetalert2";
import {
  getTemplates,
  selectTemplateById,
  templateState as templateSliceState,
} from "./templateSlice";
import { getAllDepartment } from "../../api/departmentApi";
import { getDepartment } from "../department/departmentSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Assignment,
  Quiz,
  Save,
  ArrowBackIos,
  Add,
  Delete,
  DragIndicator,
  ExpandMore,
  ExpandLess,
  Folder,
  FolderOpen,
} from "@mui/icons-material";
import { IoCloseCircle, IoAddCircle } from "react-icons/io5";
import ViewTemplateDetails from "./ViewTemplateDetails";

const AddEditTemplate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [actionType, setActionType] = useState("ADD");
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [editTemplateId, setEditTemplateId] = useState();
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState({});
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [submitReferenceState, setSubmitReferenceState] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [questionGroups, setQuestionGroups] = useState([]);
  const [searchParams] = useSearchParams();

  const initialTemplateState = {
    id: null,
    templateTitle: "",
    businessUnit: "",
    questionGroups: [],
    appraisalQuestions: [],
  };

  const [templateState, setTemplateState] = useState(initialTemplateState);

  const stateOfTemplate = useSelector(templateSliceState);
  const templates = useSelector(getTemplates);
  const allDepartments = useSelector(getDepartment);

  const id = searchParams.get("id");
  const editTemplate = useSelector((state) => selectTemplateById(state, id));

  useEffect(() => {
    const id = searchParams.get("id");
    const action = searchParams.get("action");
    if (id) {
      setEditTemplateId(searchParams.get("id"));
    }
    if (action) {
      setActionType(action);
      if (action === "VIEW") {
        handleView(id);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(getAllDepartment());
    dispatch(getAllTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (!stateOfTemplate.error && submitReferenceState) {
      resetStates();
      navigate(-1);
    }
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  useEffect(() => {
    const departmentOptions = allDepartments?.map((dept) => ({
      value: dept.id,
      label: dept.departmentName,
    }));
    setBusinessUnitOptions(departmentOptions);
  }, [allDepartments]);

  useEffect(() => {
    if (editTemplate) {
      const ObjectToEdit = JSON.parse(JSON.stringify(editTemplate));
      // Organize questions by groups
      const groups = organizeQuestionsByGroups(ObjectToEdit.appraisalQuestions || []);
      setQuestionGroups(groups);
      setTemplateState({
        ...ObjectToEdit,
        questionGroups: groups,
      });
      setEditTemplateId(ObjectToEdit?.id);
      setSelectedBusinessUnit({
        label: allDepartments?.find(
          (obj) => obj.id === ObjectToEdit?.businessUnit
        )?.departmentName,
        value: ObjectToEdit?.businessUnit,
      });
      // Expand all groups by default when editing
      const allGroupNames = new Set(groups.map(g => g.groupName));
      setExpandedGroups(allGroupNames);
    }
  }, [editTemplate, allDepartments]);

  const organizeQuestionsByGroups = (questions) => {
    const groupsMap = new Map();
    questions.forEach((q, index) => {
      const groupName = q.questionGroup || "General";
      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, {
          groupName,
          questions: [],
        });
      }
      groupsMap.get(groupName).questions.push({
        ...q,
        questionOrder: q.questionOrder || index,
      });
    });
    return Array.from(groupsMap.values()).map(group => ({
      ...group,
      questions: group.questions.sort((a, b) => (a.questionOrder || 0) - (b.questionOrder || 0)),
    }));
  };

  const validateStringField = (key, value) => {
    if (typeof value === "string" && key !== "id") {
      if (!value || value.trim() === "") {
        return `${formatLabel(key)} is required`;
      }
    }
    return "";
  };

  const validateQuestionOptions = (question, qIndex) => {
    const errors = {};
    if (question.optionType !== 0 && question.questionOptions) {
      question.questionOptions.forEach((optionObj, oIndex) => {
        const optionValue = optionObj?.["optionTitle"];
        if (!optionValue || optionValue.trim() === "") {
          errors[`option${qIndex + 1}-${oIndex + 1}`] = `Option ${
            oIndex + 1
          } is required`;
        }
      });
      if (question.questionOptions.length < 2) {
        errors[`question${qIndex + 1}_options`] = "At least 2 options are required";
      }
    }
    return errors;
  };

  const validateAppraisalQuestions = (questions) => {
    const errors = {};
    questions.forEach((question, qIndex) => {
      if (!question.questionTitle || question.questionTitle.trim() === "") {
        errors[`question${qIndex + 1}`] = `Question${qIndex + 1} is required`;
      }
      const optionErrors = validateQuestionOptions(question, qIndex);
      Object.assign(errors, optionErrors);
    });
    return errors;
  };

  const validateField = (key, value) => {
    const stringError = validateStringField(key, value);
    if (stringError) return stringError;
    return "";
  };

  const validateTemplate = () => {
    const validationErrors = {};
    Object.keys(templateState).forEach((key) => {
      const value = templateState[key];
      const fieldError = validateField(key, value);
      if (fieldError) {
        validationErrors[key] = fieldError;
      }
    });
    
    // Validate all questions from groups
    const allQuestions = getAllQuestions();
    const questionErrors = validateAppraisalQuestions(allQuestions);
    Object.assign(validationErrors, questionErrors);
    
    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const getAllQuestions = () => {
    return questionGroups.flatMap(group => group.questions);
  };

  const handleAddGroup = () => {
    const groupName = `Group ${questionGroups.length + 1}`;
    const newGroup = {
      groupName,
      questions: [],
    };
    setQuestionGroups([...questionGroups, newGroup]);
    setExpandedGroups(new Set([...expandedGroups, groupName]));
  };

  const handleDeleteGroup = (groupIndex) => {
    const newGroups = questionGroups.filter((_, index) => index !== groupIndex);
    setQuestionGroups(newGroups);
    const newExpanded = new Set(expandedGroups);
    newExpanded.delete(questionGroups[groupIndex].groupName);
    setExpandedGroups(newExpanded);
  };

  const handleAddQuestion = (groupIndex) => {
    const newQuestion = {
      id: null,
      questionTitle: "",
      questionSubTitle: "",
      optionType: 1,
      isRequired: false,
      questionGroup: questionGroups[groupIndex]?.groupName || "General",
      questionOrder: questionGroups[groupIndex]?.questions.length || 0,
      questionOptions: [
        { id: null, optionTitle: "" },
        { id: null, optionTitle: "" },
      ],
    };

    const updatedGroups = [...questionGroups];
    updatedGroups[groupIndex].questions.push(newQuestion);
    setQuestionGroups(updatedGroups);
  };

  const handleDeleteQuestion = (groupIndex, qIndex) => {
    const updatedGroups = [...questionGroups];
    updatedGroups[groupIndex].questions = updatedGroups[groupIndex].questions.filter(
      (_, index) => index !== qIndex
    );
    setQuestionGroups(updatedGroups);
  };

  const handleAddOption = (groupIndex, qIndex) => {
    const updatedGroups = [...questionGroups];
    const question = updatedGroups[groupIndex].questions[qIndex];
    question.questionOptions.push({ id: null, optionTitle: "" });
    setQuestionGroups(updatedGroups);
  };

  const handleDeleteOption = (groupIndex, qIndex, oIndex) => {
    const updatedGroups = [...questionGroups];
    const question = updatedGroups[groupIndex].questions[qIndex];
    question.questionOptions = question.questionOptions.filter(
      (_, index) => index !== oIndex
    );
    setQuestionGroups(updatedGroups);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const questionMatch = name.match(/question(\d+)-(\d+)/);
    const optionMatch = name.match(/option(\d+)-(\d+)-(\d+)/);
    const descriptionMatch = name.match(/description(\d+)-(\d+)/);
    const groupMatch = name.match(/groupName-(\d+)/);

    if (groupMatch) {
      const gIndex = parseInt(groupMatch[1]);
      const updatedGroups = [...questionGroups];
      updatedGroups[gIndex].groupName = value;
      updatedGroups[gIndex].questions.forEach(q => {
        q.questionGroup = value;
      });
      setQuestionGroups(updatedGroups);
    } else if (questionMatch) {
      const gIndex = parseInt(questionMatch[1]);
      const qIndex = parseInt(questionMatch[2]) - 1;
      const updatedGroups = [...questionGroups];
      updatedGroups[gIndex].questions[qIndex].questionTitle = value;
      setQuestionGroups(updatedGroups);
    } else if (optionMatch) {
      const gIndex = parseInt(optionMatch[1]);
      const qIndex = parseInt(optionMatch[2]) - 1;
      const oIndex = parseInt(optionMatch[3]) - 1;
      const updatedGroups = [...questionGroups];
      updatedGroups[gIndex].questions[qIndex].questionOptions[oIndex][
        `optionTitle`
      ] = value;
      setQuestionGroups(updatedGroups);
    } else if (descriptionMatch) {
      const gIndex = parseInt(descriptionMatch[1]);
      const qIndex = parseInt(descriptionMatch[2]) - 1;
      const updatedGroups = [...questionGroups];
      updatedGroups[gIndex].questions[qIndex].questionSubTitle = value;
      setQuestionGroups(updatedGroups);
    } else {
      setTemplateState((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleQuestionTypeChange = (value, groupIndex, qIndex) => {
    const updatedGroups = [...questionGroups];
    const question = updatedGroups[groupIndex].questions[qIndex];
    question.optionType = parseInt(value);
    if (parseInt(value) === 0) {
      question.questionOptions = [];
    } else if (question.questionOptions.length < 2) {
      question.questionOptions = [
        { id: null, optionTitle: "" },
        { id: null, optionTitle: "" },
      ];
    }
    setQuestionGroups(updatedGroups);
  };

  const handleRequiredChange = (checked, groupIndex, qIndex) => {
    const updatedGroups = [...questionGroups];
    updatedGroups[groupIndex].questions[qIndex].isRequired = checked;
    setQuestionGroups(updatedGroups);
  };

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleBusinessUnitChange = (option) => {
    setSelectedBusinessUnit(option);
    setTemplateState((prevValues) => ({
      ...prevValues,
      businessUnit: option?.value,
    }));
  };

  const handleDropdownChange = (name) => (option) => {
    switch (name) {
      case "businessUnit":
        handleBusinessUnitChange(option);
        break;
      default:
        break;
    }
  };

  const resetStates = () => {
    setTemplateState(initialTemplateState);
    setQuestionGroups([]);
    setEditTemplateId(null);
    setSelectedBusinessUnit({});
    setFormErrors({});
    setExpandedGroups(new Set());
  };

  const handleView = (id) => {
    const selectedTemplatetoView = templates.find((item) => item?.id === id);
    setSelectedTemplate(selectedTemplatetoView);
  };

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const resetForm = () => {
    setTemplateState(initialTemplateState);
    setQuestionGroups([]);
    setFormErrors({});
    setSelectedBusinessUnit({});
    setExpandedGroups(new Set());
  };

  const handleCancelClick = () => {
    navigate("/admin/template");
  };

  const handleSaveTemplate = () => {
    // Flatten questions from groups and ensure proper ordering
    const allQuestions = getAllQuestions();
    
    // Update questionOrder to reflect the actual order in the flattened array
    // Also convert checkbox type (3) to multiple choice (2) since checkbox is removed
    const questionsWithOrder = allQuestions.map((question, index) => ({
      ...question,
      questionOrder: index,
      optionType: question.optionType === 3 ? 2 : question.optionType, // Convert checkbox to multiple choice
    }));
    
    const templateToSave = {
      ...templateState,
      appraisalQuestions: questionsWithOrder,
    };

    if (validateTemplate()) {
      if (editTemplateId) {
        dispatch(
          createTemplate({
            editId: editTemplateId,
            data: trimObjectValues(templateToSave),
          })
        )
          .unwrap()
          .then(() => {
            setSubmitReferenceState(true);
          })
          .catch((error) => {
            Swal.fire({
              title: "Error!",
              text: error?.message || "Failed to update template. Please try again.",
              icon: "error",
              confirmButtonColor: "#3C50E0",
            });
          });
      } else {
        dispatch(
          createTemplate({
            editId: null,
            data: trimObjectValues(templateToSave),
          })
        )
          .unwrap()
          .then(() => {
            setSubmitReferenceState(true);
          })
          .catch((error) => {
            Swal.fire({
              title: "Error!",
              text: error?.message || "Failed to create template. Please try again.",
              icon: "error",
              confirmButtonColor: "#3C50E0",
            });
          });
      }
    } else {
      Swal.fire({
        title: "Validation Error!",
        text: "Please fill in all required fields correctly.",
        icon: "warning",
        confirmButtonColor: "#3C50E0",
      });
    }
  };

  // Initialize with a default group if no groups exist
  useEffect(() => {
    if (questionGroups.length === 0 && actionType === "ADD") {
      setQuestionGroups([{ groupName: "General", questions: [] }]);
      setExpandedGroups(new Set(["General"]));
    }
  }, [actionType]);

  return (
    <>
      {(actionType === "EDIT" || actionType === "ADD") && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-gray-50 to-blue-100 py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Back Button */}
            <div className="mb-4">
              <button
                onClick={handleCancelClick}
                className="flex items-center gap-2 text-[#135e96] hover:text-[#0b3a5c] font-semibold transition-all duration-200 group"
              >
                <ArrowBackIos className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                <span>Back</span>
              </button>
            </div>

            {/* Template Configuration Section */}
            <Card className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#0b3a5c] to-[#135e96] py-4 px-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Assignment className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Template Configuration</h3>
                    <p className="text-blue-100 text-sm">Configure template title and business unit</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    name="templateTitle"
                    label="Template Title"
                    onChange={handleInputChange}
                    value={templateState?.templateTitle}
                    placeholder="Enter template title"
                    requiredField={true}
                    errorMessage={
                      !templateState?.templateTitle?.trim() &&
                      formErrors?.templateTitle
                    }
                  />
                  <Dropdown
                    submitRef={true}
                    defaultValue={selectedBusinessUnit || null}
                    options={businessUnitOptions}
                    handleChange={handleDropdownChange("businessUnit")}
                    requiredField={true}
                    defaultLabel="Select Business Unit"
                    label="Business Unit"
                    errorMessage={
                      !templateState?.businessUnit && formErrors?.businessUnit
                    }
                  />
                </div>
              </div>
            </Card>

            {/* Questions Management Section */}
            <Card className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#0b3a5c] to-[#135e96] py-4 px-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Quiz className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Appraisal Questions</h3>
                      <p className="text-blue-100 text-sm">Organize questions into groups for better structure</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddGroup}
                      className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 text-white text-sm font-medium backdrop-blur-sm"
                    >
                      <Folder className="w-4 h-4" />
                      <span>Add Group</span>
                    </button>
                    {questionGroups.length > 0 && (
                      <button
                        onClick={() => {
                          const firstGroupIndex = 0;
                          handleAddQuestion(firstGroupIndex);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 text-white text-sm font-medium backdrop-blur-sm"
                      >
                        <IoAddCircle className="w-4 h-4" />
                        <span>Add Question</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {questionGroups.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Folder className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium mb-2">No question groups yet</p>
                    <p className="text-gray-500 text-sm mb-4">Start by adding a group to organize your questions</p>
                    <button
                      onClick={handleAddGroup}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] text-white rounded-lg hover:shadow-lg transition-all duration-200 font-semibold"
                    >
                      <Add className="w-5 h-5" />
                      <span>Create First Group</span>
                    </button>
                  </div>
                ) : (
                  questionGroups.map((group, groupIndex) => (
                    <div
                      key={`group-${groupIndex}`}
                      className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
                    >
                      {/* Group Header */}
                      <div
                        className="bg-gradient-to-r from-[#0b3a5c] to-[#135e96] px-6 py-4 cursor-pointer"
                        onClick={() => toggleGroup(group.groupName)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="flex items-center gap-2">
                              {expandedGroups.has(group.groupName) ? (
                                <ExpandLess className="w-5 h-5 text-white" />
                              ) : (
                                <ExpandMore className="w-5 h-5 text-white" />
                              )}
                              <FolderOpen className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                name={`groupName-${groupIndex}`}
                                value={group.groupName}
                                onChange={handleInputChange}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg px-4 py-2 text-white font-bold text-lg placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 w-full"
                                placeholder="Group Name"
                              />
                            </div>
                            <div className="flex items-center gap-2 text-white/90">
                              <span className="text-sm font-medium">
                                {group.questions.length} {group.questions.length === 1 ? "Question" : "Questions"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddQuestion(groupIndex);
                              }}
                              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm"
                              title="Add Question"
                            >
                              <IoAddCircle className="w-5 h-5 text-white" />
                            </button>
                            {questionGroups.length > 1 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteGroup(groupIndex);
                                }}
                                className="p-2 bg-red-500/80 hover:bg-red-600 rounded-lg transition-colors"
                                title="Delete Group"
                              >
                                <Delete className="w-5 h-5 text-white" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Group Questions */}
                      {expandedGroups.has(group.groupName) && (
                        <div className="p-6 space-y-4 animate-fadeIn">
                          {group.questions.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <Quiz className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-600 font-medium mb-2">No questions in this group</p>
                              <button
                                onClick={() => handleAddQuestion(groupIndex)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#135e96] text-white rounded-lg hover:bg-[#0b3a5c] transition-colors text-sm font-medium"
                              >
                                <Add className="w-4 h-4" />
                                <span>Add Question</span>
                              </button>
                            </div>
                          ) : (
                            group.questions.map((question, qIndex) => (
                              <div
                                key={`question-${groupIndex}-${qIndex}`}
                                className="bg-white rounded-xl border-2 border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden relative group"
                              >
                                {/* Question Delete Button */}
                                {group.questions.length > 0 && (
                                  <button
                                    onClick={() => handleDeleteQuestion(groupIndex, qIndex)}
                                    className="absolute z-10 -right-2 -top-2 p-2 bg-red-500 hover:bg-red-600 rounded-full transition-all duration-200 shadow-lg opacity-0 group-hover:opacity-100"
                                  >
                                    <IoCloseCircle className="text-white text-xl" />
                                  </button>
                                )}

                                {/* Question Header */}
                                <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-6 py-4 border-b border-gray-200">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-gradient-to-br from-[#0b3a5c] to-[#135e96] text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                                        {qIndex + 1}
                                      </div>
                                      <h4 className="text-base font-bold text-gray-800">
                                        Question {qIndex + 1}
                                      </h4>
                                    </div>
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <div className="w-full sm:w-auto">
                                        <Dropdown
                                          options={[
                                            { value: 0, label: "Text Input" },
                                            { value: 1, label: "Single Choice" },
                                            { value: 2, label: "Multiple Choice" },
                                          ]}
                                          defaultValue={{
                                            value: question.optionType === 3 ? 2 : question.optionType, // Convert checkbox (3) to multiple choice (2)
                                            label:
                                              question.optionType === 0
                                                ? "Text Input"
                                                : question.optionType === 1
                                                ? "Single Choice"
                                                : "Multiple Choice", // Treat both 2 and 3 as Multiple Choice
                                          }}
                                          handleChange={(option) =>
                                            handleQuestionTypeChange(option.value, groupIndex, qIndex)
                                          }
                                          defaultLabel="Select Question Type"
                                          label="Question Type"
                                        />
                                      </div>
                                      {question.optionType !== 0 && (
                                        <button
                                          onClick={() => handleAddOption(groupIndex, qIndex)}
                                          className="flex items-center gap-2 px-4 py-2 bg-[#135e96] hover:bg-[#0b3a5c] text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                                        >
                                          <IoAddCircle className="w-4 h-4" />
                                          <span>Add Option</span>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Question Content */}
                                <div className="p-6 space-y-4">
                                  {/* Question Title */}
                                  <div className="flex items-start gap-4">
                                    <div className="flex-1">
                                      <Input
                                        name={`question${groupIndex}-${qIndex + 1}`}
                                        label="Question Title"
                                        value={question.questionTitle}
                                        onChange={handleInputChange}
                                        placeholder="Enter your question here..."
                                        errorMessage={
                                          !question?.questionTitle?.trim() &&
                                          formErrors?.[`question${qIndex + 1}`]
                                        }
                                        requiredField
                                      />
                                    </div>
                                    <div className="pt-8">
                                      <label className="flex items-center space-x-2 cursor-pointer bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                                        <input
                                          type="checkbox"
                                          checked={question.isRequired || false}
                                          onChange={(e) =>
                                            handleRequiredChange(e.target.checked, groupIndex, qIndex)
                                          }
                                          className="w-4 h-4 text-[#135e96] border-gray-300 rounded focus:ring-[#135e96]"
                                        />
                                        <span className="text-sm font-medium text-gray-700">
                                          Required
                                        </span>
                                      </label>
                                    </div>
                                  </div>

                                  {/* Question Description */}
                                  <Input
                                    name={`description${groupIndex}-${qIndex + 1}`}
                                    label="Description (Optional)"
                                    value={question.questionSubTitle}
                                    onChange={handleInputChange}
                                    placeholder="Add additional context or instructions..."
                                  />

                                  {/* Options Section */}
                                  {question.optionType !== 0 && (
                                    <div className="space-y-4">
                                      <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                                        <div className="w-3 h-3 bg-[#135e96] rounded-full"></div>
                                        <h5 className="text-sm font-bold text-gray-700">
                                          Answer Options
                                        </h5>
                                      </div>

                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {question.questionOptions?.map(
                                          (optionObj, oIndex) => {
                                            const optionValue =
                                              optionObj?.["optionTitle"] || "";
                                            return (
                                              <div
                                                className="relative group/option"
                                                key={`option-${groupIndex}-${qIndex}-${oIndex}`}
                                              >
                                                <Input
                                                  requiredField
                                                  name={`option${groupIndex}-${qIndex + 1}-${oIndex + 1}`}
                                                  label={`Option ${String.fromCharCode(65 + oIndex)}`}
                                                  value={optionValue}
                                                  onChange={handleInputChange}
                                                  placeholder={`Enter option ${String.fromCharCode(65 + oIndex)}...`}
                                                  errorMessage={
                                                    !optionValue?.trim() &&
                                                    formErrors?.[
                                                      `option${qIndex + 1}-${oIndex + 1}`
                                                    ]
                                                  }
                                                />
                                                {question.questionOptions?.length > 2 &&
                                                  oIndex > 1 && (
                                                    <button
                                                      onClick={() =>
                                                        handleDeleteOption(groupIndex, qIndex, oIndex)
                                                      }
                                                      className="absolute right-2 top-10 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover/option:opacity-100"
                                                    >
                                                      <TiDelete className="h-5 w-5" />
                                                    </button>
                                                  )}
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                      {formErrors?.[`question${qIndex + 1}_options`] && (
                                        <p className="text-red-500 text-sm mt-2 font-medium">
                                          {formErrors[`question${qIndex + 1}_options`]}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-center sm:justify-end gap-4 pt-4">
              <Button
                variant="outline"
                value="Reset the form"
                handleClick={resetForm}
                disabled={stateOfTemplate?.loading}
                className="w-full sm:w-auto order-2 sm:order-1 px-8 py-3"
              />
              <Button
                variant="primary"
                value={editTemplateId ? "Update Template" : "Create Template"}
                disabled={stateOfTemplate?.loading || false}
                handleClick={handleSaveTemplate}
                loading={stateOfTemplate?.loading}
                icon={<Save className="w-5 h-5" />}
                className="w-full sm:w-auto order-1 sm:order-2 px-8 py-3"
              />
            </div>
          </div>
        </div>
      )}
      {actionType === "VIEW" && (
        <ViewTemplateDetails selectedTemplate={selectedTemplate} />
      )}
    </>
  );
};

export default AddEditTemplate;
